import { store } from './store';
import SocketFactory from '../socketFactory';
import {
  connectionEstablished,
  update_incomingOrders,
  update_outgoingOrders,
  update_poolOrders,
  update_notifications,
  update_messages,
  connectionLost,
  setWhatsAppCodeVerificationStatus,
  updateNewConnection,
} from './features/sockets/socketslice';
import { updateOrder } from './features/transaction/remittanceSlice';

let socket;
let connectionSubscription;

const EVENT_POOL_ORDERS = 'pool orders';
const EVENT_INCOMING_ORDERS = 'incoming orders';
const EVENT_OUTGOING_ORDERS = 'outgoing orders';
const EVENT_NOTIFICATIONS = 'notifications';
const EVENT_WHATSAPP_VERIFICATION_STATUS = 'whatsAppVerificationStatus';

// Function to set up socket event listeners
const setupSocketListeners = (socket, dispatch, order) => {
  // Set up event listeners for orders socket (only if it exists for agents)
  if (socket.socketOrders) {
    socket.socketOrders.on('connect', () => {
      console.log('Connected to server-orders');
      dispatch(connectionEstablished());
    });

    socket.socketOrders.on('disconnect', (reason) => {
      console.log('Disconnected from orders socket:', reason);
      dispatch(connectionLost());
    });

    socket.socketOrders.on('connect_error', (error) => {
      console.log('Error connecting to orders socket:', error);
      dispatch(connectionLost());
      dispatch(updateNewConnection());
    });

    socket.socketOrders.on('reconnect', (attemptNumber) => {
      console.log('Reconnected to orders socket, attempt:', attemptNumber);
      dispatch(connectionEstablished());
    });

    socket.socketOrders.on(EVENT_POOL_ORDERS, (data) => {
      console.log('Received pool orders:', data);
      dispatch(update_poolOrders(data));
    });

    socket.socketOrders.on(EVENT_INCOMING_ORDERS, (data) => {
      console.log('Received incoming orders:', data);
      dispatch(update_incomingOrders(data));
    });

    socket.socketOrders.on(EVENT_OUTGOING_ORDERS, (data) => {
      console.log('Received outgoing orders:', data);
      dispatch(update_outgoingOrders(data));
    });

    if (order?.txId) {
      socket.socketOrders.on(`message-${order?.txId}`, (data) => {
        console.log('Got messages for txId:', order?.txId, data);
        dispatch(update_messages({ txId: order?.txId, data: data }));
      });

      socket.socketOrders.on(`${order?.txId}`, (data) => {
        console.log('Got order update for txId:', order?.txId, data);
        dispatch(updateOrder(data[0]));
      });
    }
  } else {
    console.log('socketOrders not available - user is not an agent');
  }

  // Set up event listeners for notifications socket
  if (socket.socketNotifications) {
    socket.socketNotifications.on('connect', () => {
      console.log('Connected to notifications socket');
    });

    socket.socketNotifications.on('disconnect', (reason) => {
      console.log('Disconnected from notifications socket:', reason);
    });

    socket.socketNotifications.on('reconnect', (attemptNumber) => {
      console.log('Reconnected to notifications socket, attempt:', attemptNumber);
    });

    socket.socketNotifications.on(EVENT_NOTIFICATIONS, (page, limit, length, number, data) => {
      console.log('Received notifications:', { page, limit, length, number, data });
      dispatch(
        update_notifications({
          page: page,
          limit: limit,
          totalItems: length,
          number: number,
          data: data,
        })
      );
    });
  }

  // WhatsApp verification socket events
  if (socket.socketVerification) {
    socket.socketVerification.on('connect', () => {
      console.log('Connected to verification socket');
    });

    socket.socketVerification.on('disconnect', (reason) => {
      console.log('Disconnected from verification socket:', reason);
    });

    socket.socketVerification.on('reconnect', (attemptNumber) => {
      console.log('Reconnected to verification socket, attempt:', attemptNumber);
    });

    socket.socketVerification.on(EVENT_WHATSAPP_VERIFICATION_STATUS, (data) => {
      console.log('WhatsApp verification status received:', data);
      dispatch(setWhatsAppCodeVerificationStatus(data.verified));
    });
  }
};

// Function to clean up socket event listeners
const cleanupSocketListeners = (socket, order) => {
  if (socket.socketOrders) {
    socket.socketOrders.off('connect');
    socket.socketOrders.off('disconnect');
    socket.socketOrders.off('connect_error');
    socket.socketOrders.off('reconnect');
    socket.socketOrders.off(EVENT_POOL_ORDERS);
    socket.socketOrders.off(EVENT_INCOMING_ORDERS);
    socket.socketOrders.off(EVENT_OUTGOING_ORDERS);
    
    if (order?.txId) {
      socket.socketOrders.off(`message-${order?.txId}`);
      socket.socketOrders.off(`${order?.txId}`);
    }
  }

  if (socket.socketNotifications) {
    socket.socketNotifications.off('connect');
    socket.socketNotifications.off('disconnect');
    socket.socketNotifications.off('reconnect');
    socket.socketNotifications.off(EVENT_NOTIFICATIONS);
  }

  if (socket.socketVerification) {
    socket.socketVerification.off('connect');
    socket.socketVerification.off('disconnect');
    socket.socketVerification.off('reconnect');
    socket.socketVerification.off(EVENT_WHATSAPP_VERIFICATION_STATUS);
  }
};

export const initializeSocketConnection = () => {
  return (dispatch, getState) => {
    const jwt = getState().user?.firebaseJWt;
    const role = getState().user?.account?.accInfo?.role == 'user' ? false : true;
    console.log('Initializing socket connection with role:', role, 'JWT:', !!jwt);
    const order = getState().remittance.order;

    // Clean up existing socket if it exists
    if (socket) {
      cleanupSocketListeners(socket, order);
      socket.disconnect();
    }

    // Create new socket connection
    socket = SocketFactory.create(jwt, role);
    console.log('Socket created, socketOrders exists:', !!socket.socketOrders);
    
    // Set up event listeners
    setupSocketListeners(socket, dispatch, order);
    
    // Connect all sockets
    socket.connect();

    // Set up store subscription to monitor changes
    if (connectionSubscription) {
      connectionSubscription();
    }

    connectionSubscription = store.subscribe(() => {
      const state = store.getState();
      const newOrder = state.remittance.order;
      const newJwt = state.user.firebaseJWt;
      const newRole = state.user?.account?.accInfo?.role == 'user' ? false : true;

      // Handle order changes
      if (newOrder && newOrder.txId !== order?.txId && socket.socketOrders) {
        console.log('Order changed, updating listeners for txId:', newOrder.txId);
        
        // Remove old listeners
        if (order?.txId) {
          socket.socketOrders.off(`message-${order?.txId}`);
          socket.socketOrders.off(`${order?.txId}`);
        }
        
        // Add new listeners
        socket.socketOrders.on(`message-${newOrder.txId}`, (data) => {
          console.log('Got messages for new txId:', newOrder.txId, data);
          dispatch(update_messages({ txId: newOrder.txId, data: data }));
        });

        socket.socketOrders.on(`${newOrder.txId}`, (data) => {
          console.log('Got order update for new txId:', newOrder.txId, data);
          dispatch(updateOrder(data[0]));
        });
      }

      // Handle JWT or role changes
      if ((newJwt && newJwt !== jwt) || (newRole !== role)) {
        console.log('JWT or role changed, recreating socket connection');
        console.log('Old JWT:', !!jwt, 'New JWT:', !!newJwt);
        console.log('Old role:', role, 'New role:', newRole);
        
        // Clean up existing socket
        if (socket) {
          cleanupSocketListeners(socket, newOrder);
          socket.disconnect();
        }
        
        // Create new socket with updated credentials and role
        socket = SocketFactory.create(newJwt, newRole);
        
        // Set up event listeners for the new socket
        setupSocketListeners(socket, dispatch, newOrder);
        
        // Connect all sockets
        socket.connect();
        
        // Log the new connection status
        setTimeout(() => {
          console.log('=== Post-role-change socket status ===');
          const status = socket.getConnectionStatus();
          console.log('Orders socket exists:', !!status.orders?.exists);
          console.log('Orders socket connected:', !!status.orders?.connected);
          console.log('Total sockets:', status.summary.total);
          console.log('Connected sockets:', status.summary.connected);
          console.log('=====================================');
        }, 1000);
      }
    });
  };
};

// Function to manually reconnect sockets
export const reconnectSockets = () => {
  return (dispatch, getState) => {
    console.log('Manual socket reconnection requested');
    const jwt = getState().user?.firebaseJWt;
    const role = getState().user?.account?.accInfo?.role == 'user' ? false : true;
    
    if (socket) {
      cleanupSocketListeners(socket, getState().remittance.order);
      socket.disconnect();
    }
    
    socket = SocketFactory.create(jwt, role);
    setupSocketListeners(socket, dispatch, getState().remittance.order);
    socket.connect();
  };
};

// Function to disconnect sockets
export const disconnectSockets = () => {
  if (socket) {
    cleanupSocketListeners(socket, null);
    socket.disconnect();
    socket = null;
  }
  
  if (connectionSubscription) {
    connectionSubscription();
    connectionSubscription = null;
  }
  
  console.log('All socket connections disconnected');
};

// Utility function to check all socket connections
export const checkSocketConnections = () => {
  const status = SocketFactory.checkAllConnections();
  console.log('=== Socket Connection Status ===');
  console.log('Timestamp:', status.timestamp);
  
  if (status.error) {
    console.error('Error:', status.error);
    return status;
  }
  
  console.log('Summary:', status.summary);
  
  // Log orders socket status
  if (status.orders) {
    if (status.orders.exists) {
      console.log('📡 Orders Socket:', {
        connected: status.orders.connected ? '✅' : '❌',
        id: status.orders.id,
        transport: status.orders.transport,
        url: status.orders.url,
        events: status.orders.events.length,
        auth: status.orders.auth.hasToken ? '✅' : '❌'
      });
    } else {
      console.log('📡 Orders Socket: ❌ Not available -', status.orders.reason);
    }
  }
  
  // Log notifications socket status
  if (status.notifications) {
    console.log('🔔 Notifications Socket:', {
      connected: status.notifications.connected ? '✅' : '❌',
      id: status.notifications.id,
      transport: status.notifications.transport,
      url: status.notifications.url,
      events: status.notifications.events.length,
      auth: status.notifications.auth.hasToken ? '✅' : '❌'
    });
  }
  
  // Log verification socket status
  if (status.verification) {
    console.log('✅ Verification Socket:', {
      connected: status.verification.connected ? '✅' : '❌',
      id: status.verification.id,
      transport: status.verification.transport,
      url: status.verification.url,
      events: status.verification.events.length,
      auth: status.verification.auth.hasToken ? '✅' : '❌'
    });
  }
  
  console.log('================================');
  return status;
};

// Utility function to get detailed health check
export const getSocketHealthCheck = () => {
  const health = SocketFactory.getHealthCheck();
  console.log('=== Socket Health Check ===');
  console.log('Overall Status:', health.overall.toUpperCase());
  
  if (health.issues.length > 0) {
    console.log('🚨 Issues:');
    health.issues.forEach(issue => console.log('  -', issue));
  }
  
  if (health.recommendations.length > 0) {
    console.log('💡 Recommendations:');
    health.recommendations.forEach(rec => console.log('  -', rec));
  }
  
  console.log('===========================');
  return health;
};

// Utility function to force reconnect all sockets
export const forceReconnectAllSockets = () => {
  console.log('🔄 Force reconnecting all sockets...');
  const result = SocketFactory.forceReconnect();
  console.log('Result:', result);
  return result;
};

// Utility function to get current socket instance
export const getCurrentSocketInstance = () => {
  return SocketFactory.getCurrentConnection();
};

// Utility function to monitor socket connections in real-time
export const startSocketMonitoring = (intervalMs = 30000) => {
  console.log('🔍 Starting socket connection monitoring...');
  
  const monitor = setInterval(() => {
    const status = checkSocketConnections();
    const health = getSocketHealthCheck();
    
    // Auto-reconnect if critical issues detected
    if (health.overall === 'critical') {
      console.log('🚨 Critical socket issues detected, attempting auto-reconnect...');
      forceReconnectAllSockets();
    }
  }, intervalMs);
  
  // Return function to stop monitoring
  return () => {
    clearInterval(monitor);
    console.log('🔍 Socket monitoring stopped');
  };
};

// Utility function to handle role changes and reconnect sockets
export const handleRoleChange = (newRole) => {
  return (dispatch, getState) => {
    const jwt = getState().user?.firebaseJWt;
    const currentRole = getState().user?.account?.accInfo?.role == 'user' ? false : true;
    
    console.log('🔄 Handling role change...');
    console.log('Current role (agent):', currentRole);
    console.log('New role (agent):', newRole);
    
    if (currentRole !== newRole) {
      console.log('Role change detected, updating socket connection...');
      
      // Clean up existing socket
      if (socket) {
        cleanupSocketListeners(socket, getState().remittance.order);
        socket.disconnect();
      }
      
      // Create new socket with updated role
      socket = SocketFactory.create(jwt, newRole);
      
      // Set up event listeners
      setupSocketListeners(socket, dispatch, getState().remittance.order);
      
      // Connect all sockets
      socket.connect();
      
      console.log('✅ Socket connection updated for new role');
      
      // Check connection status after role change
      setTimeout(() => {
        const status = checkSocketConnections();
        if (newRole && !status.orders?.connected) {
          console.log('⚠️ Orders socket not connected after role change to agent');
        }
      }, 2000);
    } else {
      console.log('No role change detected');
    }
  };
};

// Utility function to check if orders socket is available and connected
export const checkOrdersSocketStatus = () => {
  const status = SocketFactory.checkAllConnections();
  
  if (status.error) {
    return { available: false, connected: false, reason: status.error };
  }
  
  if (!status.orders?.exists) {
    return { available: false, connected: false, reason: 'User is not an agent' };
  }
  
  return {
    available: true,
    connected: status.orders.connected,
    id: status.orders.id,
    transport: status.orders.transport,
    url: status.orders.url
  };
};
 